import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  BookOpen,
  Bell,
  CreditCard,
  TrendingUp,
  Settings,
  Plus,
  Edit,
  Trash2,
  Calendar,
  Mail,
  Phone,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Dumbbell,
  UtensilsCrossed,
  ChevronRight,
} from 'lucide-react';
import * as adminService from '../services/adminService';
import { shouldAllowOperation, recordOperation } from '../utils/rateLimiter';
import PlanSettings from './plans/AdminConfiguration/PlanSettings';
import { supabase } from '../lib/supabase';
import { isStripeConfigured } from '../lib/stripe';
import { calculateRenewalDate, formatRenewalDate, formatRenewalPeriod, getServicePriceSuffix } from '../utils/paymentUtils';

/**
 * Client Management Modal
 * Comprehensive modal for managing all aspects of a client
 * Features tab-based sidebar navigation
 */
export default function ClientManagementModal({ client, adminUserId, isOpen, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Data states
  const [programs, setPrograms] = useState([]);
  const [notices, setNotices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [progressEntries, setProgressEntries] = useState([]);

  // Fetch all client data when modal opens
  useEffect(() => {
    if (isOpen && client && adminUserId) {
      loadClientData();
    }
  }, [isOpen, client?.id, adminUserId]);

  const loadClientData = async () => {
    if (!adminUserId || !client) return;
    
    setIsLoading(true);
    setError('');

    try {
      const [programsResult, noticesResult, paymentsResult, progressResult] = await Promise.all([
        adminService.getAllPrograms(adminUserId),
        adminService.getAllNotices(adminUserId),
        adminService.getAllPayments(adminUserId),
        adminService.getAllProgressEntries(adminUserId, client.id),
      ]);

      // Filter data for this specific client
      if (programsResult.success) {
        setPrograms(programsResult.data.filter(p => p.user_id === client.id));
      }
      if (noticesResult.success) {
        setNotices(noticesResult.data.filter(n => n.user_id === client.id));
      }
      if (paymentsResult.success) {
        setPayments(paymentsResult.data.filter(p => p.user_id === client.id));
      }
      if (progressResult.success) {
        setProgressEntries(progressResult.data);
      }
    } catch (err) {
      console.error('Error loading client data:', err);
      setError('Failed to load client data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    loadClientData();
    if (onRefresh) onRefresh();
  };

  if (!isOpen || !client) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'plans', label: 'Plans', icon: BookOpen },
    { id: 'notices', label: 'Notices', icon: Bell },
    { id: 'programs', label: 'Programs', icon: BookOpen },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-red-600 to-red-700 text-white">
            <div>
              <h2 className="text-2xl font-bold">{client.full_name || 'Client'}</h2>
              <p className="text-red-100 text-sm mt-1">{client.email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Main Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
              <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full px-4 py-3 text-left rounded-lg transition-all flex items-center gap-3 ${
                        isActive
                          ? 'bg-red-50 text-red-600 border-l-4 border-red-600 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto bg-white">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading client data...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="p-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <AnimatePresence mode="wait">
                    {activeTab === 'overview' && (
                      <OverviewTab
                        key="overview"
                        client={client}
                        programs={programs}
                        notices={notices}
                        payments={payments}
                        progressEntries={progressEntries}
                      />
                    )}
                    {activeTab === 'plans' && (
                      <PlansTab
                        key="plans"
                        client={client}
                        adminUserId={adminUserId}
                        programs={programs}
                        onRefresh={handleRefresh}
                      />
                    )}
                    {activeTab === 'notices' && (
                      <NoticesTab
                        key="notices"
                        client={client}
                        adminUserId={adminUserId}
                        notices={notices}
                        onRefresh={handleRefresh}
                      />
                    )}
                    {activeTab === 'programs' && (
                      <ProgramsTab
                        key="programs"
                        client={client}
                        adminUserId={adminUserId}
                        programs={programs}
                        onRefresh={handleRefresh}
                      />
                    )}
                    {activeTab === 'payments' && (
                      <PaymentsTab
                        key="payments"
                        client={client}
                        adminUserId={adminUserId}
                        payments={payments}
                        onRefresh={handleRefresh}
                      />
                    )}
                    {activeTab === 'progress' && (
                      <ProgressTab
                        key="progress"
                        client={client}
                        adminUserId={adminUserId}
                        progressEntries={progressEntries}
                        onRefresh={handleRefresh}
                      />
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

// Overview Tab Component
function OverviewTab({ client, programs, notices, payments, progressEntries }) {
  const activePrograms = programs.filter(p => p.status === 'active').length;
  const unreadNotices = notices.filter(n => !n.read_at).length;
  const totalRevenue = payments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <h3 className="text-2xl font-bold text-gray-900">Client Overview</h3>

      {/* Client Information Card */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Full Name</p>
              <p className="font-medium text-gray-900">{client.full_name || 'Not set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium text-gray-900">{client.email}</p>
            </div>
          </div>
          {client.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{client.phone}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Role</p>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                client.role === 'admin' ? 'bg-red-100 text-red-800' :
                client.role === 'editor' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {client.role}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Member Since</p>
              <p className="font-medium text-gray-900">
                {new Date(client.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium text-gray-600">Total Programs</h5>
            <BookOpen className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{programs.length}</p>
          <p className="text-xs text-gray-500 mt-1">{activePrograms} active</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium text-gray-600">Unread Notices</h5>
            <Bell className="w-5 h-5 text-yellow-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{unreadNotices}</p>
          <p className="text-xs text-gray-500 mt-1">of {notices.length} total</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium text-gray-600">Total Revenue</h5>
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ${totalRevenue.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 mt-1">{payments.length} payments</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium text-gray-600">Progress Entries</h5>
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{progressEntries.length}</p>
          <p className="text-xs text-gray-500 mt-1">tracked</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h4>
        <div className="space-y-3">
          {[...programs, ...notices, ...payments]
            .sort((a, b) => new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at))
            .slice(0, 5)
            .map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  {item.program_name && <BookOpen className="w-4 h-4 text-red-600" />}
                  {item.title && <Bell className="w-4 h-4 text-red-600" />}
                  {item.amount && <CreditCard className="w-4 h-4 text-red-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {item.program_name || item.title || `Payment: $${item.amount}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(item.created_at || item.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          {programs.length === 0 && notices.length === 0 && payments.length === 0 && (
            <p className="text-center text-gray-500 py-4">No recent activity</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Plans Tab Component - Enhanced with workout and meal plan builders
function PlansTab({ client, adminUserId, programs, onRefresh }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [builderView, setBuilderView] = useState(null); // 'workout', 'meal', 'exercise', 'config'
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programForm, setProgramForm] = useState({
    programName: '',
    programType: 'training_protocol',
    status: 'active',
    startDate: '',
    endDate: '',
    coachNotes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleEdit = (program) => {
    setEditingProgram(program);
    setProgramForm({
      programName: program.program_name,
      programType: program.program_type,
      status: program.status,
      startDate: program.start_date ? program.start_date.split('T')[0] : '',
      endDate: program.end_date ? program.end_date.split('T')[0] : '',
      coachNotes: program.coach_notes || '',
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (programId) => {
    if (!confirm('Are you sure you want to delete this program?')) return;
    if (!adminUserId) return;

    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      alert(rateCheck.error);
      return;
    }

    recordOperation('admin_operation');

    const result = await adminService.deleteProgram(adminUserId, programId);
    if (result.success) {
      onRefresh();
    } else {
      alert(result.error || 'Failed to delete program');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    setFormError('');
    setIsSubmitting(true);

    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      setFormError(rateCheck.error);
      setIsSubmitting(false);
      return;
    }

    recordOperation('admin_operation');

    try {
      const result = await adminService.upsertProgram(adminUserId, {
        id: editingProgram?.id,
        userId: client.id,
        programName: programForm.programName,
        programType: programForm.programType,
        status: programForm.status,
        startDate: programForm.startDate,
        endDate: programForm.endDate || null,
        coachNotes: programForm.coachNotes,
      });

      if (result.success) {
        setShowCreateForm(false);
        setEditingProgram(null);
        setProgramForm({
          programName: '',
          programType: 'training_protocol',
          status: 'active',
          startDate: '',
          endDate: '',
          coachNotes: '',
        });
        onRefresh();
      } else {
        setFormError(result.error || 'Failed to save program');
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (selectedPlan && builderView) {
    return (
      <PlanBuilderView
        plan={selectedPlan}
        builderView={builderView}
        adminUserId={adminUserId}
        onBack={() => {
          setSelectedPlan(null);
          setBuilderView(null);
        }}
        onRefresh={onRefresh}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900">Plans</h3>
        <button
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingProgram(null);
            setProgramForm({
              programName: '',
              programType: 'training_protocol',
              status: 'active',
              startDate: '',
              endDate: '',
              coachNotes: '',
            });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Plan
        </button>
      </div>

      {/* Create/Edit Plan Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            {editingProgram ? 'Edit Plan' : 'Create Plan'}
          </h4>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {formError}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={programForm.programName}
                onChange={(e) => setProgramForm({ ...programForm, programName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={programForm.programType}
                  onChange={(e) => setProgramForm({ ...programForm, programType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="coaching">Coaching</option>
                  <option value="diet_plan">Diet Plan</option>
                  <option value="training_protocol">Training Protocol</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={programForm.status}
                  onChange={(e) => setProgramForm({ ...programForm, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={programForm.startDate}
                  onChange={(e) => setProgramForm({ ...programForm, startDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date (optional)
                </label>
                <input
                  type="date"
                  value={programForm.endDate}
                  onChange={(e) => setProgramForm({ ...programForm, endDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coach Notes
              </label>
              <textarea
                rows={4}
                value={programForm.coachNotes}
                onChange={(e) => setProgramForm({ ...programForm, coachNotes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Add notes about this plan..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : editingProgram ? 'Update Plan' : 'Create Plan'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingProgram(null);
                  setFormError('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Programs List with Builder Actions */}
      <div className="space-y-4">
        {programs.length > 0 ? (
          programs.map((program) => (
            <div key={program.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{program.program_name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      program.status === 'active' ? 'bg-green-100 text-green-800' :
                      program.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {program.status}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                      {program.program_type.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                    <span>Start: {new Date(program.start_date).toLocaleDateString()}</span>
                    {program.end_date && (
                      <span>End: {new Date(program.end_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(program)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(program.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Builder Actions */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setSelectedPlan(program);
                    setBuilderView('workout');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Dumbbell className="w-4 h-4" />
                  <span className="text-sm font-medium">Workouts</span>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </button>
                <button
                  onClick={() => {
                    setSelectedPlan(program);
                    setBuilderView('meal');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <UtensilsCrossed className="w-4 h-4" />
                  <span className="text-sm font-medium">Meals</span>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </button>
                <button
                  onClick={() => {
                    setSelectedPlan(program);
                    setBuilderView('exercise');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <Dumbbell className="w-4 h-4" />
                  <span className="text-sm font-medium">Exercises</span>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </button>
                <button
                  onClick={() => {
                    setSelectedPlan(program);
                    setBuilderView('config');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span className="text-sm font-medium">Settings</span>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No programs assigned to this client</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Plan Builder View Component
function PlanBuilderView({ plan, builderView, adminUserId, onBack, onRefresh }) {
  const [workouts, setWorkouts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [meals, setMeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    if (plan) {
      loadPlanData();
    }
  }, [plan]);

  const loadPlanData = async () => {
    setIsLoading(true);
    try {
      const [workoutsResult, exercisesResult, mealsResult] = await Promise.all([
        supabase.from('plan_workouts').select('*').eq('program_id', plan.id),
        supabase.from('plan_exercises').select('*').or(`program_id.eq.${plan.id},is_global.eq.true`),
        supabase.from('plan_meals').select('*').eq('program_id', plan.id),
      ]);

      if (workoutsResult.data) setWorkouts(workoutsResult.data);
      if (exercisesResult.data) setExercises(exercisesResult.data);
      if (mealsResult.data) setMeals(mealsResult.data);
    } catch (error) {
      console.error('Error loading plan data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            ← Back
          </button>
          <h3 className="text-2xl font-bold text-gray-900">{plan.program_name}</h3>
          <span className="text-sm text-gray-500 capitalize">
            {builderView === 'workout' && 'Workout Builder'}
            {builderView === 'meal' && 'Meal Plan Builder'}
            {builderView === 'exercise' && 'Exercise Library'}
            {builderView === 'config' && 'Plan Settings'}
          </span>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add {builderView === 'workout' ? 'Workout' : builderView === 'meal' ? 'Meal' : builderView === 'exercise' ? 'Exercise' : ''}
        </button>
      </div>

      {builderView === 'workout' && (
        <WorkoutBuilder
          planId={plan.id}
          workouts={workouts}
          exercises={exercises}
          adminUserId={adminUserId}
          onRefresh={loadPlanData}
        />
      )}

      {builderView === 'meal' && (
        <MealPlanBuilder
          planId={plan.id}
          meals={meals}
          adminUserId={adminUserId}
          onRefresh={loadPlanData}
        />
      )}

      {builderView === 'exercise' && (
        <ExerciseBuilder
          planId={plan.id}
          exercises={exercises}
          adminUserId={adminUserId}
          onRefresh={loadPlanData}
        />
      )}

      {builderView === 'config' && (
        <PlanSettings planId={plan.id} onSave={onRefresh} />
      )}
    </div>
  );
}

// Workout Builder Component
function WorkoutBuilder({ planId, workouts, exercises, adminUserId, onRefresh }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [workoutForm, setWorkoutForm] = useState({
    workoutName: '',
    workoutType: 'strength',
    scheduledDate: '',
    weekNumber: '',
    dayNumber: '',
    durationMinutes: '',
    restDay: false,
    instructions: '',
    exerciseOrder: [],
  });

  const handleCreateWorkout = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    try {
      const result = await adminService.createWorkout(adminUserId, planId, {
        workoutName: workoutForm.workoutName,
        workoutType: workoutForm.workoutType,
        scheduledDate: workoutForm.scheduledDate || null,
        weekNumber: workoutForm.weekNumber ? parseInt(workoutForm.weekNumber) : null,
        dayNumber: workoutForm.dayNumber ? parseInt(workoutForm.dayNumber) : null,
        durationMinutes: workoutForm.durationMinutes ? parseInt(workoutForm.durationMinutes) : null,
        restDay: workoutForm.restDay,
        instructions: workoutForm.instructions || null,
        exerciseOrder: workoutForm.exerciseOrder,
      });

      if (result.success) {
        setShowCreateForm(false);
        setWorkoutForm({
          workoutName: '',
          workoutType: 'strength',
          scheduledDate: '',
          weekNumber: '',
          dayNumber: '',
          durationMinutes: '',
          restDay: false,
          instructions: '',
          exerciseOrder: [],
        });
        onRefresh();
      }
    } catch (error) {
      console.error('Error creating workout:', error);
    }
  };

  return (
    <div className="space-y-6">
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Create Workout</h4>
          <form onSubmit={handleCreateWorkout} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workout Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={workoutForm.workoutName}
                onChange={(e) => setWorkoutForm({ ...workoutForm, workoutName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., Upper Body Strength"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workout Type
                </label>
                <select
                  value={workoutForm.workoutType}
                  onChange={(e) => setWorkoutForm({ ...workoutForm, workoutType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="strength">Strength</option>
                  <option value="cardio">Cardio</option>
                  <option value="hiit">HIIT</option>
                  <option value="mobility">Mobility</option>
                  <option value="recovery">Recovery</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scheduled Date
                </label>
                <input
                  type="date"
                  value={workoutForm.scheduledDate}
                  onChange={(e) => setWorkoutForm({ ...workoutForm, scheduledDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Week Number
                </label>
                <input
                  type="number"
                  value={workoutForm.weekNumber}
                  onChange={(e) => setWorkoutForm({ ...workoutForm, weekNumber: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day Number
                </label>
                <input
                  type="number"
                  value={workoutForm.dayNumber}
                  onChange={(e) => setWorkoutForm({ ...workoutForm, dayNumber: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (min)
                </label>
                <input
                  type="number"
                  value={workoutForm.durationMinutes}
                  onChange={(e) => setWorkoutForm({ ...workoutForm, durationMinutes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="60"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="restDay"
                checked={workoutForm.restDay}
                onChange={(e) => setWorkoutForm({ ...workoutForm, restDay: e.target.checked })}
                className="w-4 h-4 text-red-600 focus:ring-red-500"
              />
              <label htmlFor="restDay" className="text-sm font-medium text-gray-700">
                Rest Day
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructions
              </label>
              <textarea
                rows={4}
                value={workoutForm.instructions}
                onChange={(e) => setWorkoutForm({ ...workoutForm, instructions: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Workout instructions and notes..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Create Workout
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Workouts List */}
      <div className="space-y-4">
        {workouts.length > 0 ? (
          workouts.map((workout) => (
            <div key={workout.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{workout.workout_name}</h4>
                  <p className="text-sm text-gray-600 capitalize">{workout.workout_type}</p>
                  {workout.scheduled_date && (
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(workout.scheduled_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <Dumbbell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No workouts created yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Meal Plan Builder Component
function MealPlanBuilder({ planId, meals, adminUserId, onRefresh }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [mealForm, setMealForm] = useState({
    mealName: '',
    mealType: 'breakfast',
    scheduledDate: '',
    weekNumber: '',
    dayNumber: '',
    mealOrder: 1,
    description: '',
    ingredients: [],
    macros: { calories: '', protein: '', carbs: '', fats: '' },
    instructions: '',
  });

  const handleCreateMeal = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    try {
      const result = await adminService.createMealPlan(adminUserId, planId, {
        mealName: mealForm.mealName,
        mealType: mealForm.mealType,
        scheduledDate: mealForm.scheduledDate || null,
        weekNumber: mealForm.weekNumber ? parseInt(mealForm.weekNumber) : null,
        dayNumber: mealForm.dayNumber ? parseInt(mealForm.dayNumber) : null,
        mealOrder: parseInt(mealForm.mealOrder),
        description: mealForm.description || null,
        ingredients: mealForm.ingredients,
        macros: {
          calories: mealForm.macros.calories ? parseFloat(mealForm.macros.calories) : null,
          protein: mealForm.macros.protein ? parseFloat(mealForm.macros.protein) : null,
          carbs: mealForm.macros.carbs ? parseFloat(mealForm.macros.carbs) : null,
          fats: mealForm.macros.fats ? parseFloat(mealForm.macros.fats) : null,
        },
        instructions: mealForm.instructions || null,
      });

      if (result.success) {
        setShowCreateForm(false);
        setMealForm({
          mealName: '',
          mealType: 'breakfast',
          scheduledDate: '',
          weekNumber: '',
          dayNumber: '',
          mealOrder: 1,
          description: '',
          ingredients: [],
          macros: { calories: '', protein: '', carbs: '', fats: '' },
          instructions: '',
        });
        onRefresh();
      }
    } catch (error) {
      console.error('Error creating meal:', error);
    }
  };

  return (
    <div className="space-y-6">
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Create Meal</h4>
          <form onSubmit={handleCreateMeal} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meal Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={mealForm.mealName}
                onChange={(e) => setMealForm({ ...mealForm, mealName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., Grilled Chicken with Vegetables"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meal Type
                </label>
                <select
                  value={mealForm.mealType}
                  onChange={(e) => setMealForm({ ...mealForm, mealType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snack">Snack</option>
                  <option value="pre_workout">Pre-Workout</option>
                  <option value="post_workout">Post-Workout</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scheduled Date
                </label>
                <input
                  type="date"
                  value={mealForm.scheduledDate}
                  onChange={(e) => setMealForm({ ...mealForm, scheduledDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Macros
              </label>
              <div className="grid grid-cols-4 gap-2">
                <input
                  type="number"
                  placeholder="Calories"
                  value={mealForm.macros.calories}
                  onChange={(e) => setMealForm({
                    ...mealForm,
                    macros: { ...mealForm.macros, calories: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Protein (g)"
                  value={mealForm.macros.protein}
                  onChange={(e) => setMealForm({
                    ...mealForm,
                    macros: { ...mealForm.macros, protein: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Carbs (g)"
                  value={mealForm.macros.carbs}
                  onChange={(e) => setMealForm({
                    ...mealForm,
                    macros: { ...mealForm.macros, carbs: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Fats (g)"
                  value={mealForm.macros.fats}
                  onChange={(e) => setMealForm({
                    ...mealForm,
                    macros: { ...mealForm.macros, fats: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructions
              </label>
              <textarea
                rows={4}
                value={mealForm.instructions}
                onChange={(e) => setMealForm({ ...mealForm, instructions: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Cooking instructions..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Create Meal
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Meals List */}
      <div className="space-y-4">
        {meals.length > 0 ? (
          meals.map((meal) => (
            <div key={meal.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{meal.meal_name}</h4>
                  <p className="text-sm text-gray-600 capitalize">{meal.meal_type.replace('_', ' ')}</p>
                  {meal.scheduled_date && (
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(meal.scheduled_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <UtensilsCrossed className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No meals created yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Exercise Builder Component
function ExerciseBuilder({ planId, exercises, adminUserId, onRefresh }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [exerciseForm, setExerciseForm] = useState({
    exerciseName: '',
    description: '',
    muscleGroups: [],
    equipmentType: 'bodyweight',
    videoUrl: '',
    instructions: '',
    defaultSets: '',
    defaultReps: '',
    defaultWeight: '',
    isGlobal: false,
  });

  const handleCreateExercise = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    try {
      const result = await adminService.createExercise(adminUserId, {
        programId: exerciseForm.isGlobal ? null : planId,
        exerciseName: exerciseForm.exerciseName,
        description: exerciseForm.description || null,
        muscleGroups: exerciseForm.muscleGroups,
        equipmentType: exerciseForm.equipmentType,
        videoUrl: exerciseForm.videoUrl || null,
        instructions: exerciseForm.instructions || null,
        defaultSets: exerciseForm.defaultSets ? parseInt(exerciseForm.defaultSets) : null,
        defaultReps: exerciseForm.defaultReps || null,
        defaultWeight: exerciseForm.defaultWeight ? parseFloat(exerciseForm.defaultWeight) : null,
        isGlobal: exerciseForm.isGlobal,
      });

      if (result.success) {
        setShowCreateForm(false);
        setExerciseForm({
          exerciseName: '',
          description: '',
          muscleGroups: [],
          equipmentType: 'bodyweight',
          videoUrl: '',
          instructions: '',
          defaultSets: '',
          defaultReps: '',
          defaultWeight: '',
          isGlobal: false,
        });
        onRefresh();
      }
    } catch (error) {
      console.error('Error creating exercise:', error);
    }
  };

  const muscleGroups = ['chest', 'back', 'shoulders', 'arms', 'legs', 'glutes', 'core', 'cardio', 'full_body'];

  return (
    <div className="space-y-6">
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Create Exercise</h4>
          <form onSubmit={handleCreateExercise} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exercise Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={exerciseForm.exerciseName}
                onChange={(e) => setExerciseForm({ ...exerciseForm, exerciseName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., Barbell Bench Press"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                rows={3}
                value={exerciseForm.description}
                onChange={(e) => setExerciseForm({ ...exerciseForm, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Exercise description..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Equipment Type
                </label>
                <select
                  value={exerciseForm.equipmentType}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, equipmentType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="bodyweight">Bodyweight</option>
                  <option value="dumbbells">Dumbbells</option>
                  <option value="barbell">Barbell</option>
                  <option value="machine">Machine</option>
                  <option value="cable">Cable</option>
                  <option value="kettlebell">Kettlebell</option>
                  <option value="resistance_bands">Resistance Bands</option>
                  <option value="cardio">Cardio</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video URL
                </label>
                <input
                  type="url"
                  value={exerciseForm.videoUrl}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, videoUrl: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isGlobal"
                checked={exerciseForm.isGlobal}
                onChange={(e) => setExerciseForm({ ...exerciseForm, isGlobal: e.target.checked })}
                className="w-4 h-4 text-red-600 focus:ring-red-500"
              />
              <label htmlFor="isGlobal" className="text-sm font-medium text-gray-700">
                Make available to all plans (Global Exercise)
              </label>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Create Exercise
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Exercises List */}
      <div className="space-y-4">
        {exercises.length > 0 ? (
          exercises.map((exercise) => (
            <div key={exercise.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{exercise.exercise_name}</h4>
                  <p className="text-sm text-gray-600 capitalize">{exercise.equipment_type.replace('_', ' ')}</p>
                  {exercise.is_global && (
                    <span className="text-xs text-blue-600 font-medium">Global Exercise</span>
                  )}
                </div>
                <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <Dumbbell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No exercises created yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Notices Tab Component
function NoticesTab({ client, adminUserId, notices, onRefresh }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [noticeForm, setNoticeForm] = useState({
    title: '',
    message: '',
    type: 'info',
    actionUrl: '',
    expiresAt: '',
  });

  const handleCreateNotice = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    setFormError('');
    setIsSubmitting(true);

    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      setFormError(rateCheck.error);
      setIsSubmitting(false);
      return;
    }

    recordOperation('admin_operation');

    try {
      const result = await adminService.createNotice(adminUserId, {
        userId: client.id,
        title: noticeForm.title,
        message: noticeForm.message,
        type: noticeForm.type,
        actionUrl: noticeForm.actionUrl || null,
        expiresAt: noticeForm.expiresAt || null,
      });

      if (result.success) {
        setShowCreateForm(false);
        setNoticeForm({
          title: '',
          message: '',
          type: 'info',
          actionUrl: '',
          expiresAt: '',
        });
        onRefresh();
      } else {
        setFormError(result.error || 'Failed to create notice');
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900">Notices</h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Send Notice
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Create Notice</h4>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {formError}
            </div>
          )}
          <form onSubmit={handleCreateNotice} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={noticeForm.title}
                onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={4}
                value={noticeForm.message}
                onChange={(e) => setNoticeForm({ ...noticeForm, message: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={noticeForm.type}
                  onChange={(e) => setNoticeForm({ ...noticeForm, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expires At (optional)
                </label>
                <input
                  type="datetime-local"
                  value={noticeForm.expiresAt}
                  onChange={(e) => setNoticeForm({ ...noticeForm, expiresAt: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action URL (optional)
              </label>
              <input
                type="url"
                value={noticeForm.actionUrl}
                onChange={(e) => setNoticeForm({ ...noticeForm, actionUrl: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="https://example.com"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Notice'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormError('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {notices.length > 0 ? (
          notices.map((notice) => (
            <div key={notice.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{notice.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      notice.type === 'error' ? 'bg-red-100 text-red-800' :
                      notice.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {notice.type}
                    </span>
                    {notice.read_at ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <p className="text-gray-700 mb-2">{notice.message}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Sent: {new Date(notice.created_at).toLocaleString()}</span>
                    {notice.expires_at && (
                      <span>Expires: {new Date(notice.expires_at).toLocaleString()}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No notices sent to this client</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Programs Tab Component
function ProgramsTab({ client, adminUserId, programs, onRefresh }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [editingProgram, setEditingProgram] = useState(null);
  const [programForm, setProgramForm] = useState({
    programName: '',
    programType: 'training',
    status: 'active',
    startDate: '',
    endDate: '',
    coachNotes: '',
  });

  const handleEdit = (program) => {
    setEditingProgram(program);
    setProgramForm({
      programName: program.program_name,
      programType: program.program_type,
      status: program.status,
      startDate: program.start_date ? program.start_date.split('T')[0] : '',
      endDate: program.end_date ? program.end_date.split('T')[0] : '',
      coachNotes: program.coach_notes || '',
    });
    setShowCreateForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    setFormError('');
    setIsSubmitting(true);

    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      setFormError(rateCheck.error);
      setIsSubmitting(false);
      return;
    }

    recordOperation('admin_operation');

    try {
      const result = await adminService.upsertProgram(adminUserId, {
        id: editingProgram?.id,
        userId: client.id,
        programName: programForm.programName,
        programType: programForm.programType,
        status: programForm.status,
        startDate: programForm.startDate,
        endDate: programForm.endDate || null,
        coachNotes: programForm.coachNotes,
      });

      if (result.success) {
        setShowCreateForm(false);
        setEditingProgram(null);
        setProgramForm({
          programName: '',
          programType: 'training',
          status: 'active',
          startDate: '',
          endDate: '',
          coachNotes: '',
        });
        onRefresh();
      } else {
        setFormError(result.error || 'Failed to save program');
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (programId) => {
    if (!confirm('Are you sure you want to delete this program?')) return;
    if (!adminUserId) return;

    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      alert(rateCheck.error);
      return;
    }

    recordOperation('admin_operation');

    const result = await adminService.deleteProgram(adminUserId, programId);
    if (result.success) {
      onRefresh();
    } else {
      alert(result.error || 'Failed to delete program');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900">Programs</h3>
        <button
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingProgram(null);
            setProgramForm({
              programName: '',
              programType: 'training',
              status: 'active',
              startDate: '',
              endDate: '',
              coachNotes: '',
            });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {editingProgram ? 'Edit Program' : 'Assign Program'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            {editingProgram ? 'Edit Program' : 'Create Program'}
          </h4>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {formError}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={programForm.programName}
                onChange={(e) => setProgramForm({ ...programForm, programName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={programForm.programType}
                  onChange={(e) => setProgramForm({ ...programForm, programType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="training">Training</option>
                  <option value="nutrition">Nutrition</option>
                  <option value="wellness">Wellness</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={programForm.status}
                  onChange={(e) => setProgramForm({ ...programForm, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={programForm.startDate}
                  onChange={(e) => setProgramForm({ ...programForm, startDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date (optional)
                </label>
                <input
                  type="date"
                  value={programForm.endDate}
                  onChange={(e) => setProgramForm({ ...programForm, endDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coach Notes
              </label>
              <textarea
                rows={4}
                value={programForm.coachNotes}
                onChange={(e) => setProgramForm({ ...programForm, coachNotes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Add notes about this program..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : editingProgram ? 'Update Program' : 'Create Program'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingProgram(null);
                  setFormError('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {programs.length > 0 ? (
          programs.map((program) => (
            <div key={program.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{program.program_name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      program.status === 'active' ? 'bg-green-100 text-green-800' :
                      program.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {program.status}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {program.program_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                    <span>Start: {new Date(program.start_date).toLocaleDateString()}</span>
                    {program.end_date && (
                      <span>End: {new Date(program.end_date).toLocaleDateString()}</span>
                    )}
                  </div>
                  {program.coach_notes && (
                    <p className="text-gray-700 mb-2">{program.coach_notes}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(program)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(program.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No programs assigned to this client</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Payments Tab Component
function PaymentsTab({ client, adminUserId, payments, onRefresh }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    currency: 'USD',
    paymentType: 'one-time',
    provider: 'stripe',
    description: '',
    renewalPeriod: 'monthly',
    customMonths: null,
    serviceId: '',
  });
  const [availableServices, setAvailableServices] = useState([]);

  // Load services when form opens
  useEffect(() => {
    if (showCreateForm && adminUserId) {
      loadServices();
    }
  }, [showCreateForm, adminUserId]);

  const loadServices = async () => {
    if (!adminUserId) return;
    try {
      const result = await adminService.getAllServices(adminUserId);
      if (result.success) {
        setAvailableServices(result.data || []);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const handleServiceChange = (serviceId) => {
    if (!serviceId) {
      setPaymentForm({ ...paymentForm, serviceId: '' });
      return;
    }

    const service = availableServices.find(s => s.id === serviceId);
    if (service) {
      setPaymentForm({
        ...paymentForm,
        serviceId: service.id,
        amount: service.price.toString(),
        currency: service.currency || 'USD',
        paymentType: 'subscription',
        description: service.name,
        renewalPeriod: service.renewal_period || 'monthly',
        customMonths: service.renewal_period === 'custom' ? service.renewal_interval_months : null,
      });
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    setFormError('');
    setIsSubmitting(true);

    // Validate Stripe configuration if using Stripe
    if (paymentForm.provider === 'stripe' && !isStripeConfigured) {
      setFormError('Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.');
      setIsSubmitting(false);
      return;
    }

    // Validate subscription renewal period
    if (paymentForm.paymentType === 'subscription') {
      if (!paymentForm.renewalPeriod) {
        setFormError('Please select a renewal period for subscription payments.');
        setIsSubmitting(false);
        return;
      }
      if (paymentForm.renewalPeriod === 'custom') {
        if (!paymentForm.customMonths || paymentForm.customMonths < 1 || paymentForm.customMonths > 24) {
          setFormError('Please enter a valid number of months between 1 and 24 for custom renewal period.');
          setIsSubmitting(false);
          return;
        }
      }
    }

    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      setFormError(rateCheck.error);
      setIsSubmitting(false);
      return;
    }

    recordOperation('admin_operation');

    try {
      // Prepare metadata with service_id if service is selected
      const metadata = paymentForm.serviceId ? { service_id: paymentForm.serviceId } : {};

      // For Stripe payments, always set status to pending
      // The client will complete the payment via Stripe Checkout
      const result = await adminService.createPayment(adminUserId, {
        userId: client.id,
        provider: paymentForm.provider,
        providerPaymentId: `admin-${Date.now()}`,
        amount: parseFloat(paymentForm.amount),
        currency: paymentForm.currency,
        status: paymentForm.provider === 'stripe' ? 'pending' : 'pending',
        paymentType: paymentForm.paymentType,
        description: paymentForm.description,
        renewalPeriod: paymentForm.paymentType === 'subscription' ? (paymentForm.renewalPeriod || 'monthly') : null,
        customMonths: paymentForm.paymentType === 'subscription' && paymentForm.renewalPeriod === 'custom' ? paymentForm.customMonths : null,
        metadata: metadata,
      });

      if (result.success) {
        setShowCreateForm(false);
        setPaymentForm({
          amount: '',
          currency: 'USD',
          paymentType: 'one-time',
          provider: 'stripe',
          description: '',
          renewalPeriod: 'monthly',
          customMonths: null,
          serviceId: '',
        });
        onRefresh();
      } else {
        setFormError(result.error || 'Failed to create payment request');
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900">Payments</h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Payment Request
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Create Payment Request</h4>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {formError}
            </div>
          )}
          
          {/* Stripe Payment Info */}
          {paymentForm.provider === 'stripe' && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Stripe Payment</p>
                  <p className="text-xs">
                    This payment will be created as "pending". The client will see it in their account 
                    and can complete it via Stripe Checkout. Status will update automatically when payment is processed.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleCreatePayment} className="space-y-4">
            {/* Service Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service (Optional)
              </label>
              <select
                value={paymentForm.serviceId || ''}
                onChange={(e) => handleServiceChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">Select a service (or create manually)</option>
                {availableServices.filter(s => s.is_active).map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} - ${parseFloat(service.price).toLocaleString()}{getServicePriceSuffix(service.renewal_period, service.renewal_interval_months)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Selecting a service will auto-populate amount, description, and renewal period</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  value={paymentForm.currency}
                  onChange={(e) => setPaymentForm({ ...paymentForm, currency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={paymentForm.paymentType}
                  onChange={(e) => {
                    const newPaymentType = e.target.value;
                    setPaymentForm({ 
                      ...paymentForm, 
                      paymentType: newPaymentType,
                      // Reset renewal fields if not subscription, otherwise default to monthly
                      renewalPeriod: newPaymentType === 'subscription' ? (paymentForm.renewalPeriod || 'monthly') : null,
                      customMonths: newPaymentType === 'subscription' && paymentForm.renewalPeriod === 'custom' ? paymentForm.customMonths : (newPaymentType === 'subscription' ? null : null),
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="one-time">One-Time</option>
                  <option value="subscription">Subscription</option>
                  <option value="installment">Installment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Provider
                </label>
                <select
                  value={paymentForm.provider}
                  onChange={(e) => setPaymentForm({ ...paymentForm, provider: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="stripe">Stripe</option>
                  <option value="affirm">Affirm</option>
                </select>
              </div>
            </div>

            {/* Subscription Renewal Period Fields */}
            {paymentForm.paymentType === 'subscription' && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <label className="block text-sm font-medium text-gray-700">
                    Renewal Period <span className="text-red-500">*</span>
                  </label>
                </div>
                
                <div>
                  <select
                    required={paymentForm.paymentType === 'subscription'}
                    value={paymentForm.renewalPeriod || 'monthly'}
                    onChange={(e) => {
                      const newPeriod = e.target.value;
                      setPaymentForm({ 
                        ...paymentForm, 
                        renewalPeriod: newPeriod,
                        customMonths: newPeriod === 'custom' ? (paymentForm.customMonths || 1) : null,
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="3months">Every 3 Months</option>
                    <option value="custom">Custom (Months)</option>
                  </select>
                </div>

                {/* Custom Months Input */}
                {paymentForm.renewalPeriod === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Months <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      required={paymentForm.renewalPeriod === 'custom'}
                      value={paymentForm.customMonths || ''}
                      onChange={(e) => {
                        const months = parseInt(e.target.value) || null;
                        if (months !== null && months >= 1 && months <= 24) {
                          setPaymentForm({ ...paymentForm, customMonths: months });
                        } else if (e.target.value === '') {
                          setPaymentForm({ ...paymentForm, customMonths: null });
                        }
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        paymentForm.renewalPeriod === 'custom' && paymentForm.customMonths !== null && (paymentForm.customMonths < 1 || paymentForm.customMonths > 24)
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter months (1-24)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter a number between 1 and 24 months</p>
                    {paymentForm.renewalPeriod === 'custom' && paymentForm.customMonths !== null && (paymentForm.customMonths < 1 || paymentForm.customMonths > 24) && (
                      <p className="text-xs text-red-600 mt-1">Please enter a value between 1 and 24</p>
                    )}
                  </div>
                )}

                {/* Renewal Date Preview */}
                {paymentForm.renewalPeriod && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs font-medium text-blue-800 mb-1">Renewal Information</p>
                    <p className="text-sm text-blue-700">
                      Period: <span className="font-medium">{formatRenewalPeriod(paymentForm.renewalPeriod, paymentForm.customMonths)}</span>
                    </p>
                    {paymentForm.renewalPeriod && (paymentForm.renewalPeriod !== 'custom' || paymentForm.customMonths) && (
                      <p className="text-sm text-blue-700">
                        Next Renewal: <span className="font-medium">
                          {formatRenewalDate(calculateRenewalDate(paymentForm.renewalPeriod, paymentForm.customMonths))}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                rows={3}
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Payment description..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create Payment Request'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormError('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {payments.length > 0 ? (
          payments.map((payment) => (
            <div key={payment.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {formatCurrency(payment.amount, payment.currency)}
                    </h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                      payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {payment.status}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {payment.payment_provider}
                    </span>
                  </div>
                  {payment.description && (
                    <p className="text-gray-700 mb-2">{payment.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Type: {payment.payment_type}</span>
                    <span>Created: {new Date(payment.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No payment records for this client</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Progress Tab Component
function ProgressTab({ client, adminUserId, progressEntries, onRefresh }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [progressForm, setProgressForm] = useState({
    entryType: 'weight',
    entryDate: new Date().toISOString().split('T')[0],
    data: '',
    notes: '',
  });

  const handleCreateProgress = async (e) => {
    e.preventDefault();
    if (!adminUserId) return;

    setFormError('');
    setIsSubmitting(true);

    const rateCheck = shouldAllowOperation('admin_operation');
    if (!rateCheck.allowed) {
      setFormError(rateCheck.error);
      setIsSubmitting(false);
      return;
    }

    recordOperation('admin_operation');

    try {
      // Parse data JSON if provided
      let parsedData = {};
      if (progressForm.data.trim()) {
        try {
          parsedData = JSON.parse(progressForm.data);
        } catch (err) {
          setFormError('Invalid JSON in data field');
          setIsSubmitting(false);
          return;
        }
      }

      const result = await adminService.createProgressEntry(adminUserId, {
        userId: client.id,
        entryType: progressForm.entryType,
        entryDate: progressForm.entryDate,
        data: parsedData,
        notes: progressForm.notes || null,
      });

      if (result.success) {
        setShowCreateForm(false);
        setProgressForm({
          entryType: 'weight',
          entryDate: new Date().toISOString().split('T')[0],
          data: '',
          notes: '',
        });
        onRefresh();
      } else {
        setFormError(result.error || 'Failed to create progress entry');
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900">Progress Entries</h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Progress Entry
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Create Progress Entry</h4>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {formError}
            </div>
          )}
          <form onSubmit={handleCreateProgress} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={progressForm.entryType}
                  onChange={(e) => setProgressForm({ ...progressForm, entryType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="weight">Weight</option>
                  <option value="measurement">Measurement</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={progressForm.entryDate}
                  onChange={(e) => setProgressForm({ ...progressForm, entryDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data (JSON format, optional)
              </label>
              <textarea
                rows={4}
                value={progressForm.data}
                onChange={(e) => setProgressForm({ ...progressForm, data: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono text-sm"
                placeholder='{"weight": 180, "bodyFat": 15}'
              />
              <p className="text-xs text-gray-500 mt-1">Enter JSON data for this entry (optional)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                rows={3}
                value={progressForm.notes}
                onChange={(e) => setProgressForm({ ...progressForm, notes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Add notes about this progress entry..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create Entry'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormError('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {progressEntries.length > 0 ? (
          progressEntries.map((entry) => (
            <div key={entry.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900 capitalize">
                      {entry.entry_type} Entry
                    </h4>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {new Date(entry.entry_date).toLocaleDateString()}
                    </span>
                  </div>
                  {entry.data && Object.keys(entry.data).length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm text-gray-600 mb-1">Data:</p>
                      <pre className="text-xs bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto">
                        {JSON.stringify(entry.data, null, 2)}
                      </pre>
                    </div>
                  )}
                  {entry.notes && (
                    <p className="text-gray-700 mb-2">{entry.notes}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Created: {new Date(entry.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No progress entries for this client</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
